import React, { useState, useEffect } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import parse from 'html-react-parser';
import { isMobile, isTablet } from 'react-device-detect';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  trackCheckInUploadFileSuccess,
  trackCheckInUploadFileError,
} from '../../../analytics';

import { InfoPoints } from '../../../components/custom';
import { Form } from '../../../components/form';
import { Button, Modal } from '../../../components/general';
import { ConfirmModal, ErrorModal } from '../../../components/modals';

import { updatePersonPhoto } from '../../../services/checkInService';

import { PublicEvent, Face } from '../../../types';
import { isEmpty } from '../../../utils';

import PersonPhoto from '../../VideoBuilder/People/components/personPhoto';
import { getTipsList, getSelfieInfoPoints } from '../components/getCheckinText';
import SelfieUpload from '../components/selfieUpload';

interface Props {
  event: PublicEvent;
  face: Face;
  handleAfterUpload?: () => void;
  refetchFace?: () => void;
  stepText?: number;
  hidePoints?: boolean;
  isPreview?: boolean;
}

const DetailsStage: React.FC<Props> = (props) => {
  const {
    event,
    face,
    handleAfterUpload,
    refetchFace,
    stepText,
    hidePoints,
    isPreview,
  } = props;

  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('isNew') === 'true';

  const [isProcessing, setIsProcessing] = useState(false);
  const [deletingPhotos, setDeletingPhotos] = useState<string[]>([]);

  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew) setShowPrivacyModal(true);
  }, [isNew]);

  const handleUpload = async (file: UploadFile) => {
    if (isPreview) return;

    setIsProcessing(true);

    try {
      await updatePersonPhoto({
        eventId: event.eventId,
        parentElementId: face.elementId,
        photo: file,
        trackOnSuccess: () => trackCheckInUploadFileSuccess('photo'),
        trackOnError: (fileName: string) =>
          trackCheckInUploadFileError('photo', fileName),
      });

      toast.success('Mission accomplished: photo uploaded');

      refetchFace && refetchFace();

      handleAfterUpload && handleAfterUpload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <React.Fragment>
      <Form formClassName="w-full">
        <h5>
          {!!stepText && parse(`<b>Step ${stepText}</b>: `)}Upload a photo or a
          selfie<span className="text-primary-900">*</span>
        </h5>

        {face.photos && !isEmpty(face.photos) && (
          <React.Fragment>
            <h6>Uploaded photos ({face.photos.length})</h6>

            <div className="flex flex-wrap gap-2">
              {face.photos.map((photo) => (
                <PersonPhoto
                  key={photo.elementId}
                  img={photo}
                  allowDelete={(face.photos?.length ?? 0) > 1}
                  deletingPhotos={deletingPhotos}
                  setDeletingPhotos={setDeletingPhotos}
                  refetchData={refetchFace}
                  isPublic
                />
              ))}
            </div>

            <hr className="my-2" />

            <h6>Upload more photos</h6>
          </React.Fragment>
        )}

        <SelfieUpload
          id="ci-photo-upload"
          onDrop={handleUpload}
          isPreview={isPreview}
        />

        {(isMobile || isTablet) && (
          <div className="text-sm italic">
            Tip: If taking a selfie doesn’t work, upload one from your gallery.
            Just snap a photo with your phone’s camera app and upload it here,
            or pick an existing one.
          </div>
        )}

        {!hidePoints && <InfoPoints points={getSelfieInfoPoints()} />}
      </Form>

      <Modal show={showPrivacyModal} onHide={() => setShowPrivacyModal(false)}>
        <Modal.Body centered>
          <h4>Your Privacy Matters</h4>

          <div>
            Don’t worry – your face is safe with us! It’s only used to spot you
            in the event footage, not for world domination or random ads. We
            won’t share or sell it. It’s kept just long enough to work its
            magic, then it’s gone.
          </div>

          <Button onClick={() => setShowPrivacyModal(false)} className="w-full">
            Got It!
          </Button>
        </Modal.Body>
      </Modal>

      <ConfirmModal
        show={isProcessing}
        isProcessing
        heading="We're uploading your photo"
        tipsList={getTipsList()}
      />

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        btn={
          <Button onClick={() => setError('')} className="w-full">
            OK
          </Button>
        }
      />
    </React.Fragment>
  );
};

export default DetailsStage;
